import { invisiblyApi } from '../api/invisibly-api';
import type {
  EtlDailyRollupResponse,
  EtlHealthPaginatedQueryParams,
  EtlHealthQueryParams,
  EtlMartVerificationPaginatedResponse,
  EtlPlatformReconciliationPaginatedResponse,
  PlatformReconciliationRow,
  EtlDataStatus,
  EtlPipelineStatus,
  PlatformHealthDetail,
  PlatformHealthResponse,
  FixInflatedLoadRequest,
  FixInflatedLoadResponse,
  VerificationPolicy,
  VerificationPolicyState,
} from '../../pages/operations/types/operations.types';
import { APITagType } from '../api/invisibly-api.types';
import type {
  CreateScheduledTaskPayload,
  RegisteredSchedulerTask,
  ScheduledTask,
  SchedulerFailuresAlert,
  TaskRunLog,
  UpdateScheduledTaskPayload,
} from '../../pages/operations/types/scheduler.types';
import type {
  CreateDataJobRunPayload,
  DataJobAlertsResponse,
  DataJobDefinition,
  DataJobRunDetail,
  DataJobRunSummary,
  DataJobRunsListParams,
  DataJobRunsPaginatedResponse,
} from '../../pages/operations/types/data-jobs.types';
import type {
  BulkLogEntryActionPayload,
  BulkLogEntryActionResponse,
  CreateLinearIssueResponse,
  LogEntriesListParams,
  LogEntriesPaginatedResponse,
  LogEntryDetail,
  LogEntrySummary,
  LogMonitoringStats,
  LogScanRun,
} from '../../pages/operations/types/log-monitoring.types';
import type {
  OperationsTemplate,
  OperationsTemplateWritePayload,
} from '../../pages/operations/types/operations-template.types';
import type {
  AdTemplate,
  AdTemplateAssistRequest,
  AdTemplateAssistResponse,
  AdTemplateWritePayload,
  ListAdTemplatesParams,
} from '../../pages/operations/types/ad-template.types';
import type { VerticalProfile, VerticalProfileVersionDetail, VerticalProfileVersionSummary, VerticalProfileWritePayload } from '../../pages/operations/types/vertical-profile.types';
import type {
  CreativeTemplate,
  CreativeTemplateVersionDetail,
  CreativeTemplateVersionSummary,
  CreativeTemplateWritePayload,
} from '../../pages/operations/types/creative-template.types';
import type {
  CreativeTemplateExample,
  CreativeTemplateExampleVersionDetail,
  CreativeTemplateExampleVersionSummary,
  CreativeTemplateExampleWritePayload,
} from '../../pages/operations/types/creative-template-example.types';

const operationsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (build) => ({
    getEtlPipelineStatus: build.query<EtlPipelineStatus, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/etl/pipeline-status',
      }),
      transformResponse: (response: { item: EtlPipelineStatus }) => response.item,
    }),
    getEtlDataStatus: build.query<EtlDataStatus, { date: string }>({
      query: ({ date }) => ({
        method: 'get',
        url: 'b2b/operations/etl/data-status',
        params: { date },
      }),
      transformResponse: (response: { item: EtlDataStatus }) => response.item,
    }),
    getPlatformHealth: build.query<PlatformHealthResponse, { date?: string }>({
      query: ({ date } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/platform-health',
        ...(date ? { params: { date } } : {}),
      }),
      transformResponse: (response: { item: PlatformHealthResponse }) => response.item,
    }),
    getPlatformHealthDetail: build.query<
    PlatformHealthDetail,
    { platform: string; date: string }
    >({
      query: ({ platform, date }) => ({
        method: 'get',
        url: 'b2b/operations/etl/platform-health/detail',
        params: { platform, date },
      }),
      transformResponse: (response: { item: PlatformHealthDetail }) => response.item,
    }),
    getEtlDailyRollup: build.query<EtlDailyRollupResponse, EtlHealthQueryParams | void>({
      query: ({ days = 30, refresh } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/daily-rollup',
        params: {
          days,
          ...(refresh ? { refresh } : {}),
        },
      }),
      transformResponse: (response: { item: EtlDailyRollupResponse }) => response.item,
    }),
    getEtlPlatformReconciliation: build.query<
    EtlPlatformReconciliationPaginatedResponse,
    EtlHealthPaginatedQueryParams | void
    >({
      query: ({
        days = 30,
        refresh,
        page = 1,
        page_size = 10,
        status_filter = 'issues',
        date,
      } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/platform-reconciliation',
        params: {
          days,
          page,
          page_size,
          status_filter,
          ...(date ? { date } : {}),
          ...(refresh ? { refresh } : {}),
        },
      }),
      transformResponse: (
        response: { item: EtlPlatformReconciliationPaginatedResponse },
      ) => response.item,
    }),
    getEtlMartVerification: build.query<
    EtlMartVerificationPaginatedResponse,
    EtlHealthPaginatedQueryParams | void
    >({
      query: ({
        days = 30,
        refresh,
        page = 1,
        page_size = 10,
        status_filter = 'issues',
        date,
      } = {}) => ({
        method: 'get',
        url: 'b2b/operations/etl/mart-verification',
        params: {
          days,
          page,
          page_size,
          status_filter,
          ...(date ? { date } : {}),
          ...(refresh ? { refresh } : {}),
        },
      }),
      transformResponse: (
        response: { item: EtlMartVerificationPaginatedResponse },
      ) => response.item,
    }),
    getEtlVerificationPolicy: build.query<VerificationPolicyState, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/etl/verification-policy',
      }),
      transformResponse: (response: { item: VerificationPolicyState }) => response.item,
      providesTags: [{ type: APITagType.EtlVerificationPolicy, id: 'CURRENT' }],
    }),
    updateEtlVerificationPolicy: build.mutation<
    VerificationPolicyState,
    { policy: VerificationPolicy } | { reset: true }
    >({
      query: (body) => ({
        method: 'patch',
        url: 'b2b/operations/etl/verification-policy',
        data: body,
      }),
      transformResponse: (response: { item: VerificationPolicyState }) => response.item,
      invalidatesTags: [{ type: APITagType.EtlVerificationPolicy, id: 'CURRENT' }],
    }),
    fixEtlInflatedLoad: build.mutation<FixInflatedLoadResponse, FixInflatedLoadRequest>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/etl/fix-inflated-load',
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: FixInflatedLoadResponse }) => response.item,
      invalidatesTags: [{ type: APITagType.DataJobRuns, id: 'LIST' }],
    }),
    getScheduledTasks: build.query<ScheduledTask[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/tasks',
      }),
      transformResponse: (response: { item: ScheduledTask[] }) => response.item,
      providesTags: [{ type: 'SchedulerTasks', id: 'LIST' }],
    }),
    getScheduledTaskRegistry: build.query<RegisteredSchedulerTask[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/tasks/registry',
      }),
      transformResponse: (response: { item: RegisteredSchedulerTask[] }) => response.item,
    }),
    getScheduledTaskLogs: build.query<TaskRunLog[], { taskId: number; limit?: number }>({
      query: ({ taskId, limit = 50 }) => ({
        method: 'get',
        url: `b2b/operations/schedules/tasks/${taskId}/logs`,
        params: { limit },
      }),
      transformResponse: (response: { item: TaskRunLog[] }) => response.item,
    }),
    getActiveScheduledTasks: build.query<ScheduledTask[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/tasks/active',
      }),
      transformResponse: (response: { item: ScheduledTask[] }) => response.item,
    }),
    getSchedulerFailures: build.query<SchedulerFailuresAlert, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/schedules/alerts/failures',
      }),
      transformResponse: (response: { item: SchedulerFailuresAlert }) => response.item,
      providesTags: [{ type: 'SchedulerTasks', id: 'FAILURES' }],
    }),
    createScheduledTask: build.mutation<ScheduledTask, CreateScheduledTaskPayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/schedules/tasks',
        data: body,
      }),
      transformResponse: (response: { item: ScheduledTask }) => response.item,
      invalidatesTags: [
        { type: 'SchedulerTasks', id: 'LIST' },
        { type: 'SchedulerTasks', id: 'FAILURES' },
      ],
    }),
    updateScheduledTask: build.mutation<ScheduledTask, { id: number; body: UpdateScheduledTaskPayload }>({
      query: ({ id, body }) => ({
        method: 'patch',
        url: `b2b/operations/schedules/tasks/${id}`,
        data: body,
      }),
      transformResponse: (response: { item: ScheduledTask }) => response.item,
      invalidatesTags: [
        { type: 'SchedulerTasks', id: 'LIST' },
        { type: 'SchedulerTasks', id: 'FAILURES' },
      ],
    }),
    deleteScheduledTask: build.mutation<{ id: number }, number>({
      query: (id) => ({
        method: 'delete',
        url: `b2b/operations/schedules/tasks/${id}`,
      }),
      transformResponse: (response: { item: { id: number } }) => response.item,
      invalidatesTags: [
        { type: 'SchedulerTasks', id: 'LIST' },
        { type: 'SchedulerTasks', id: 'FAILURES' },
      ],
    }),
    runScheduledTaskNow: build.mutation<TaskRunLog, number>({
      query: (taskId) => ({
        method: 'post',
        url: `b2b/operations/schedules/tasks/${taskId}/run-now`,
      }),
      transformResponse: (response: { item: TaskRunLog }) => response.item,
      async onQueryStarted(taskId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            operationsEndpoints.util.updateQueryData(
              'getScheduledTaskLogs',
              { taskId },
              (draft) => {
                const existing = draft.find((entry) => entry.id === data.id);
                if (!existing) {
                  draft.unshift(data);
                }
              },
            ),
          );
        } catch {
          // Caller handles errors.
        }
      },
    }),
    getDataJobDefinitions: build.query<DataJobDefinition[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/data-jobs/definitions',
      }),
      transformResponse: (response: { item: DataJobDefinition[] }) => response.item,
    }),
    getDataJobRuns: build.query<DataJobRunsPaginatedResponse, DataJobRunsListParams | void>({
      query: (params = {}) => ({
        method: 'get',
        url: 'b2b/operations/data-jobs/runs',
        params,
      }),
      transformResponse: (
        response: { item: DataJobRunSummary[] | DataJobRunsPaginatedResponse },
      ): DataJobRunsPaginatedResponse => {
        const payload = response.item;
        if (Array.isArray(payload)) {
          return {
            items: payload,
            total_objects: payload.length,
            total_pages: 1,
            page: 1,
            page_size: payload.length,
          };
        }
        return payload;
      },
      providesTags: [{ type: 'DataJobRuns', id: 'LIST' }],
    }),
    getDataJobRun: build.query<DataJobRunDetail, { runId: string }>({
      query: ({ runId }) => ({
        method: 'get',
        url: `b2b/operations/data-jobs/runs/${runId}`,
      }),
      transformResponse: (response: { item: DataJobRunDetail }) => response.item,
      providesTags: (_result, _error, { runId }) => [{ type: 'DataJobRuns', id: runId }],
    }),
    createDataJobRun: build.mutation<DataJobRunDetail, CreateDataJobRunPayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/data-jobs/runs',
        data: body,
      }),
      transformResponse: (response: { item: DataJobRunDetail }) => response.item,
      invalidatesTags: [
        { type: 'DataJobRuns', id: 'LIST' },
        { type: 'DataJobRuns', id: 'FAILURES' },
      ],
    }),
    rerunDataJobRun: build.mutation<DataJobRunDetail, { runId: string; parameterOverrides?: Record<string, unknown> }>({
      query: ({ runId, parameterOverrides }) => ({
        method: 'post',
        url: `b2b/operations/data-jobs/runs/${runId}/rerun`,
        data: parameterOverrides ? { parameter_overrides: parameterOverrides } : {},
      }),
      transformResponse: (response: { item: DataJobRunDetail }) => response.item,
      invalidatesTags: [
        { type: 'DataJobRuns', id: 'LIST' },
        { type: 'DataJobRuns', id: 'FAILURES' },
      ],
    }),
    getDataJobFailures: build.query<DataJobAlertsResponse, { hours?: number } | void>({
      query: (params = {}) => ({
        method: 'get',
        url: 'b2b/operations/data-jobs/alerts/failures',
        params,
      }),
      transformResponse: (response: { item: DataJobAlertsResponse }) => response.item,
      providesTags: [{ type: 'DataJobRuns', id: 'FAILURES' }],
    }),
    getDataJobWarnings: build.query<DataJobAlertsResponse, { hours?: number } | void>({
      query: (params = {}) => ({
        method: 'get',
        url: 'b2b/operations/data-jobs/alerts/warnings',
        params,
      }),
      transformResponse: (response: { item: DataJobAlertsResponse }) => response.item,
      providesTags: [{ type: 'DataJobRuns', id: 'WARNINGS' }],
    }),
    getLogEntries: build.query<LogEntriesPaginatedResponse, LogEntriesListParams | void>({
      query: (params = {}) => ({
        method: 'get',
        url: 'b2b/operations/logs',
        params,
      }),
      transformResponse: (response: { item: LogEntriesPaginatedResponse }) => response.item,
      providesTags: [{ type: 'LogEntries', id: 'LIST' }],
    }),
    getLogEntry: build.query<LogEntryDetail, { entryId: string }>({
      query: ({ entryId }) => ({
        method: 'get',
        url: `b2b/operations/logs/${entryId}`,
      }),
      transformResponse: (response: { item: LogEntryDetail }) => response.item,
      providesTags: (_result, _error, { entryId }) => [{ type: 'LogEntries', id: entryId }],
    }),
    getLogMonitoringStats: build.query<LogMonitoringStats, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/logs/stats',
      }),
      transformResponse: (response: { item: LogMonitoringStats }) => response.item,
      providesTags: [{ type: 'LogEntries', id: 'STATS' }],
    }),
    getLogScanRuns: build.query<LogScanRun[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/logs/scan-runs',
      }),
      transformResponse: (response: { item: LogScanRun[] }) => response.item,
      providesTags: [{ type: 'LogEntries', id: 'SCAN_RUNS' }],
    }),
    scanLogsNow: build.mutation<{ enqueued_services: string[] }, void>({
      query: () => ({
        method: 'post',
        url: 'b2b/operations/logs/scan-now',
      }),
      transformResponse: (response: { item: { enqueued_services: string[] } }) => response.item,
      invalidatesTags: [
        { type: 'LogEntries', id: 'LIST' },
        { type: 'LogEntries', id: 'SCAN_RUNS' },
      ],
    }),
    ignoreLogEntry: build.mutation<LogEntryDetail, { entryId: string }>({
      query: ({ entryId }) => ({
        method: 'post',
        url: `b2b/operations/logs/${entryId}/ignore`,
      }),
      transformResponse: (response: { item: LogEntryDetail }) => response.item,
      invalidatesTags: (_result, _error, { entryId }) => [
        { type: 'LogEntries', id: 'LIST' },
        { type: 'LogEntries', id: 'STATS' },
        { type: 'LogEntries', id: entryId },
      ],
    }),
    markLogEntryFixed: build.mutation<LogEntryDetail, { entryId: string }>({
      query: ({ entryId }) => ({
        method: 'post',
        url: `b2b/operations/logs/${entryId}/mark-fixed`,
      }),
      transformResponse: (response: { item: LogEntryDetail }) => response.item,
      invalidatesTags: (_result, _error, { entryId }) => [
        { type: 'LogEntries', id: 'LIST' },
        { type: 'LogEntries', id: 'STATS' },
        { type: 'LogEntries', id: entryId },
      ],
    }),
    createLogLinearIssue: build.mutation<CreateLinearIssueResponse, { entryId: string }>({
      query: ({ entryId }) => ({
        method: 'post',
        url: `b2b/operations/logs/${entryId}/create-linear-issue`,
      }),
      transformResponse: (response: { item: CreateLinearIssueResponse }) => response.item,
      invalidatesTags: (_result, _error, { entryId }) => [
        { type: 'LogEntries', id: 'LIST' },
        { type: 'LogEntries', id: 'STATS' },
        { type: 'LogEntries', id: entryId },
      ],
    }),
    restoreLogEntry: build.mutation<LogEntryDetail, { entryId: string }>({
      query: ({ entryId }) => ({
        method: 'post',
        url: `b2b/operations/logs/${entryId}/restore`,
      }),
      transformResponse: (response: { item: LogEntryDetail }) => response.item,
      invalidatesTags: (_result, _error, { entryId }) => [
        { type: 'LogEntries', id: 'LIST' },
        { type: 'LogEntries', id: 'STATS' },
        { type: 'LogEntries', id: entryId },
      ],
    }),
    bulkLogEntryAction: build.mutation<BulkLogEntryActionResponse, BulkLogEntryActionPayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/logs/bulk-action',
        data: body,
      }),
      transformResponse: (response: { item: BulkLogEntryActionResponse }) => response.item,
      invalidatesTags: [
        { type: 'LogEntries', id: 'LIST' },
        { type: 'LogEntries', id: 'STATS' },
      ],
    }),
    getOperationsTemplates: build.query<OperationsTemplate[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/templates',
      }),
      transformResponse: (response: { item: OperationsTemplate[] }) => response.item,
      providesTags: [{ type: 'OperationsTemplates', id: 'LIST' }],
    }),
    getOperationsTemplate: build.query<OperationsTemplate, { templateId: string }>({
      query: ({ templateId }) => ({
        method: 'get',
        url: `b2b/operations/templates/${templateId}`,
      }),
      transformResponse: (response: { item: OperationsTemplate }) => response.item,
      providesTags: (_result, _error, { templateId }) => [
        { type: 'OperationsTemplates', id: templateId },
      ],
    }),
    createOperationsTemplate: build.mutation<OperationsTemplate, OperationsTemplateWritePayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/templates',
        data: body,
      }),
      transformResponse: (response: { item: OperationsTemplate }) => response.item,
      invalidatesTags: [
        { type: 'OperationsTemplates', id: 'LIST' },
        { type: 'FlowTemplates', id: 'LIST' },
      ],
    }),
    updateOperationsTemplate: build.mutation<
    OperationsTemplate,
    { templateId: string; body: Partial<OperationsTemplateWritePayload> }
    >({
      query: ({ templateId, body }) => ({
        method: 'patch',
        url: `b2b/operations/templates/${templateId}`,
        data: body,
      }),
      transformResponse: (response: { item: OperationsTemplate }) => response.item,
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'OperationsTemplates', id: 'LIST' },
        { type: 'OperationsTemplates', id: templateId },
        { type: 'FlowTemplates', id: 'LIST' },
      ],
    }),
    deleteOperationsTemplate: build.mutation<{ id: string; is_active: boolean }, string>({
      query: (templateId) => ({
        method: 'delete',
        url: `b2b/operations/templates/${templateId}`,
      }),
      transformResponse: (response: { item: { id: string; is_active: boolean } }) => response.item,
      invalidatesTags: [
        { type: 'OperationsTemplates', id: 'LIST' },
        { type: 'FlowTemplates', id: 'LIST' },
      ],
    }),
    getAdTemplates: build.query<AdTemplate[], ListAdTemplatesParams | void>({
      query: (params) => ({
        method: 'get',
        url: 'b2b/operations/ad-templates',
        params: params ?? {},
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: AdTemplate[] }) => response.item,
      providesTags: [{ type: 'AdTemplates', id: 'LIST' }],
    }),
    getAdTemplate: build.query<AdTemplate, { templateId: string }>({
      query: ({ templateId }) => ({
        method: 'get',
        url: `b2b/operations/ad-templates/${templateId}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: AdTemplate }) => response.item,
      providesTags: (_result, _error, { templateId }) => [
        { type: 'AdTemplates', id: templateId },
      ],
    }),
    createAdTemplate: build.mutation<AdTemplate, AdTemplateWritePayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/ad-templates',
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: AdTemplate }) => response.item,
      invalidatesTags: [{ type: 'AdTemplates', id: 'LIST' }],
    }),
    updateAdTemplate: build.mutation<
    AdTemplate,
    { templateId: string; body: Partial<AdTemplateWritePayload> }
    >({
      query: ({ templateId, body }) => ({
        method: 'patch',
        url: `b2b/operations/ad-templates/${templateId}`,
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: AdTemplate }) => response.item,
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'AdTemplates', id: 'LIST' },
        { type: 'AdTemplates', id: templateId },
      ],
    }),
    archiveAdTemplate: build.mutation<AdTemplate, string>({
      query: (templateId) => ({
        method: 'delete',
        url: `b2b/operations/ad-templates/${templateId}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: AdTemplate }) => response.item,
      invalidatesTags: [{ type: 'AdTemplates', id: 'LIST' }],
    }),
    assistAdTemplate: build.mutation<AdTemplateAssistResponse, AdTemplateAssistRequest>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/ad-templates/assist',
        data: body,
        handleErrorGlobally: false,
        timeout: 120_000,
      }),
      transformResponse: (response: { item: AdTemplateAssistResponse }) => response.item,
    }),
    getVerticalProfiles: build.query<VerticalProfile[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/vertical-profiles',
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: VerticalProfile[] }) => response.item,
      providesTags: [{ type: 'VerticalProfiles', id: 'LIST' }],
    }),
    getVerticalProfile: build.query<VerticalProfile, { profileId: string }>({
      query: ({ profileId }) => ({
        method: 'get',
        url: `b2b/operations/vertical-profiles/${profileId}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: VerticalProfile }) => response.item,
      providesTags: (_result, _error, { profileId }) => [
        { type: 'VerticalProfiles', id: profileId },
      ],
    }),
    createVerticalProfile: build.mutation<VerticalProfile, VerticalProfileWritePayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/vertical-profiles',
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: VerticalProfile }) => response.item,
      invalidatesTags: [{ type: 'VerticalProfiles', id: 'LIST' }],
    }),
    updateVerticalProfile: build.mutation<
    VerticalProfile,
    { profileId: string; body: VerticalProfileWritePayload }
    >({
      query: ({ profileId, body }) => ({
        method: 'patch',
        url: `b2b/operations/vertical-profiles/${profileId}`,
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: VerticalProfile }) => response.item,
      invalidatesTags: (_result, _error, { profileId }) => [
        { type: 'VerticalProfiles', id: 'LIST' },
        { type: 'VerticalProfiles', id: profileId },
      ],
    }),
    getVerticalProfileVersions: build.query<
    VerticalProfileVersionSummary[],
    { profileId: string }
    >({
      query: ({ profileId }) => ({
        method: 'get',
        url: `b2b/operations/vertical-profiles/${profileId}/versions`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: VerticalProfileVersionSummary[] }) => response.item,
      providesTags: (_result, _error, { profileId }) => [
        { type: 'VerticalProfiles', id: `${profileId}-versions` },
      ],
    }),
    getVerticalProfileVersion: build.query<
    VerticalProfileVersionDetail,
    { profileId: string; versionNumber: number }
    >({
      query: ({ profileId, versionNumber }) => ({
        method: 'get',
        url: `b2b/operations/vertical-profiles/${profileId}/versions/${versionNumber}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: VerticalProfileVersionDetail }) => response.item,
    }),
    getCreativeTemplates: build.query<CreativeTemplate[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/creative-templates',
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplate[] }) => response.item,
      providesTags: [{ type: 'CreativeTemplates', id: 'LIST' }],
    }),
    getCreativeTemplate: build.query<CreativeTemplate, { templateId: string }>({
      query: ({ templateId }) => ({
        method: 'get',
        url: `b2b/operations/creative-templates/${templateId}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplate }) => response.item,
      providesTags: (_result, _error, { templateId }) => [
        { type: 'CreativeTemplates', id: templateId },
      ],
    }),
    createCreativeTemplate: build.mutation<CreativeTemplate, CreativeTemplateWritePayload>({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/creative-templates',
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplate }) => response.item,
      invalidatesTags: [{ type: 'CreativeTemplates', id: 'LIST' }],
    }),
    updateCreativeTemplate: build.mutation<
    CreativeTemplate,
    { templateId: string; body: CreativeTemplateWritePayload }
    >({
      query: ({ templateId, body }) => ({
        method: 'patch',
        url: `b2b/operations/creative-templates/${templateId}`,
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplate }) => response.item,
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'CreativeTemplates', id: 'LIST' },
        { type: 'CreativeTemplates', id: templateId },
      ],
    }),
    getCreativeTemplateVersions: build.query<
    CreativeTemplateVersionSummary[],
    { templateId: string }
    >({
      query: ({ templateId }) => ({
        method: 'get',
        url: `b2b/operations/creative-templates/${templateId}/versions`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateVersionSummary[] }) => response.item,
      providesTags: (_result, _error, { templateId }) => [
        { type: 'CreativeTemplates', id: `${templateId}-versions` },
      ],
    }),
    getCreativeTemplateVersion: build.query<
    CreativeTemplateVersionDetail,
    { templateId: string; versionNumber: number }
    >({
      query: ({ templateId, versionNumber }) => ({
        method: 'get',
        url: `b2b/operations/creative-templates/${templateId}/versions/${versionNumber}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateVersionDetail }) => response.item,
    }),
    getCreativeTemplateExamples: build.query<CreativeTemplateExample[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/operations/creative-template-examples',
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateExample[] }) => response.item,
      providesTags: [{ type: 'CreativeTemplateExamples', id: 'LIST' }],
    }),
    getCreativeTemplateExample: build.query<CreativeTemplateExample, { exampleId: string }>({
      query: ({ exampleId }) => ({
        method: 'get',
        url: `b2b/operations/creative-template-examples/${exampleId}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateExample }) => response.item,
      providesTags: (_result, _error, { exampleId }) => [
        { type: 'CreativeTemplateExamples', id: exampleId },
      ],
    }),
    createCreativeTemplateExample: build.mutation<
    CreativeTemplateExample,
    CreativeTemplateExampleWritePayload
    >({
      query: (body) => ({
        method: 'post',
        url: 'b2b/operations/creative-template-examples',
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateExample }) => response.item,
      invalidatesTags: [{ type: 'CreativeTemplateExamples', id: 'LIST' }],
    }),
    updateCreativeTemplateExample: build.mutation<
    CreativeTemplateExample,
    { exampleId: string; body: CreativeTemplateExampleWritePayload }
    >({
      query: ({ exampleId, body }) => ({
        method: 'patch',
        url: `b2b/operations/creative-template-examples/${exampleId}`,
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateExample }) => response.item,
      invalidatesTags: (_result, _error, { exampleId }) => [
        { type: 'CreativeTemplateExamples', id: 'LIST' },
        { type: 'CreativeTemplateExamples', id: exampleId },
      ],
    }),
    getCreativeTemplateExampleVersions: build.query<
    CreativeTemplateExampleVersionSummary[],
    { exampleId: string }
    >({
      query: ({ exampleId }) => ({
        method: 'get',
        url: `b2b/operations/creative-template-examples/${exampleId}/versions`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateExampleVersionSummary[] }) => response.item,
      providesTags: (_result, _error, { exampleId }) => [
        { type: 'CreativeTemplateExamples', id: `${exampleId}-versions` },
      ],
    }),
    getCreativeTemplateExampleVersion: build.query<
    CreativeTemplateExampleVersionDetail,
    { exampleId: string; versionNumber: number }
    >({
      query: ({ exampleId, versionNumber }) => ({
        method: 'get',
        url: `b2b/operations/creative-template-examples/${exampleId}/versions/${versionNumber}`,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: CreativeTemplateExampleVersionDetail }) => response.item,
    }),
  }),
});

export const {
  useGetEtlPipelineStatusQuery,
  useGetEtlDataStatusQuery,
  useGetPlatformHealthQuery,
  useGetPlatformHealthDetailQuery,
  useGetEtlDailyRollupQuery,
  useGetEtlPlatformReconciliationQuery,
  useGetEtlMartVerificationQuery,
  useGetEtlVerificationPolicyQuery,
  useUpdateEtlVerificationPolicyMutation,
  useFixEtlInflatedLoadMutation,
  useGetScheduledTasksQuery,
  useGetScheduledTaskRegistryQuery,
  useGetScheduledTaskLogsQuery,
  useGetActiveScheduledTasksQuery,
  useGetSchedulerFailuresQuery,
  useCreateScheduledTaskMutation,
  useUpdateScheduledTaskMutation,
  useDeleteScheduledTaskMutation,
  useRunScheduledTaskNowMutation,
  useGetDataJobDefinitionsQuery,
  useGetDataJobRunsQuery,
  useGetDataJobRunQuery,
  useCreateDataJobRunMutation,
  useRerunDataJobRunMutation,
  useGetDataJobFailuresQuery,
  useGetDataJobWarningsQuery,
  useGetLogEntriesQuery,
  useGetLogEntryQuery,
  useGetLogMonitoringStatsQuery,
  useGetLogScanRunsQuery,
  useScanLogsNowMutation,
  useIgnoreLogEntryMutation,
  useMarkLogEntryFixedMutation,
  useCreateLogLinearIssueMutation,
  useRestoreLogEntryMutation,
  useBulkLogEntryActionMutation,
  useGetOperationsTemplatesQuery,
  useGetOperationsTemplateQuery,
  useCreateOperationsTemplateMutation,
  useUpdateOperationsTemplateMutation,
  useDeleteOperationsTemplateMutation,
  useGetAdTemplatesQuery,
  useGetAdTemplateQuery,
  useCreateAdTemplateMutation,
  useUpdateAdTemplateMutation,
  useArchiveAdTemplateMutation,
  useAssistAdTemplateMutation,
  useGetVerticalProfilesQuery,
  useGetVerticalProfileQuery,
  useCreateVerticalProfileMutation,
  useUpdateVerticalProfileMutation,
  useGetVerticalProfileVersionsQuery,
  useGetVerticalProfileVersionQuery,
  useGetCreativeTemplatesQuery,
  useGetCreativeTemplateQuery,
  useCreateCreativeTemplateMutation,
  useUpdateCreativeTemplateMutation,
  useGetCreativeTemplateVersionsQuery,
  useGetCreativeTemplateVersionQuery,
  useGetCreativeTemplateExamplesQuery,
  useGetCreativeTemplateExampleQuery,
  useCreateCreativeTemplateExampleMutation,
  useUpdateCreativeTemplateExampleMutation,
  useGetCreativeTemplateExampleVersionsQuery,
  useGetCreativeTemplateExampleVersionQuery,
} = operationsEndpoints;

export default operationsEndpoints;
